// File share links — controlled public distribution for private files.
// Tokens are one-time-visible bearer secrets. Only CreateFileShareResponse
// returns the raw token / URL; management DTOs may use `url: null` after
// creation and never expose the stored token hash or passcode hash.
import { z } from 'zod';
import { FilePreview } from './file.js';
const Iso = z.string().datetime({ offset: true });
export const FileShareTargetKind = z.enum(['file', 'folder']);
export const FileShare = z.object({
    id: z.string().uuid(),
    ownerId: z.string().uuid(),
    targetKind: FileShareTargetKind,
    fileId: z.string().uuid().nullable(),
    folderId: z.string().uuid().nullable(),
    title: z.string().max(255).nullable(),
    url: z.string().min(1).nullable(),
    requiresPasscode: z.boolean(),
    allowDownload: z.boolean(),
    maxAccessCount: z.number().int().positive().nullable(),
    accessCount: z.number().int().nonnegative(),
    downloadCount: z.number().int().nonnegative(),
    lastAccessedAt: Iso.nullable(),
    availableAt: Iso.nullable(),
    expiresAt: Iso.nullable(),
    revokedAt: Iso.nullable(),
    createdAt: Iso,
    updatedAt: Iso,
});
export const FileShareScheduleFile = z.object({
    id: z.string().uuid(),
    folderId: z.string().uuid().nullable(),
    name: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
});
export const FileShareScheduleFolder = z.object({
    id: z.string().uuid(),
    parentId: z.string().uuid().nullable(),
    name: z.string(),
});
export const FileShareScheduledFile = z.object({
    id: z.string().uuid(),
    shareId: z.string().uuid(),
    targetKind: z.literal('file'),
    fileId: z.string().uuid(),
    folderId: z.null(),
    releaseAt: Iso,
    file: FileShareScheduleFile,
    folder: z.null(),
    createdAt: Iso,
    updatedAt: Iso,
});
export const FileShareScheduledFolder = z.object({
    id: z.string().uuid(),
    shareId: z.string().uuid(),
    targetKind: z.literal('folder'),
    fileId: z.null(),
    folderId: z.string().uuid(),
    releaseAt: Iso,
    file: z.null(),
    folder: FileShareScheduleFolder,
    createdAt: Iso,
    updatedAt: Iso,
});
export const FileShareScheduledTarget = z.discriminatedUnion('targetKind', [
    FileShareScheduledFile,
    FileShareScheduledFolder,
]);
export const FileShareScheduleCandidate = z.object({
    targetKind: z.enum(['file', 'folder']),
    file: FileShareScheduleFile.nullable(),
    folder: FileShareScheduleFolder.nullable(),
    folderPath: z.array(z.string()),
});
export const PublicFileShareFile = z.object({
    id: z.string().uuid(),
    name: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    preview: FilePreview.nullable(),
});
export const PublicFileShareFolder = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().max(2000).nullable(),
    childFolderCount: z.number().int().nonnegative(),
    childFileCount: z.number().int().nonnegative(),
    updatedAt: Iso,
});
export const PublicFileShareAncestor = z.object({
    id: z.string().uuid(),
    name: z.string(),
});
export const PublicFileShare = z.object({
    id: z.string().uuid(),
    targetKind: FileShareTargetKind,
    title: z.string().max(255).nullable(),
    requiresPasscode: z.boolean(),
    isAvailable: z.boolean(),
    availableAt: Iso.nullable(),
    allowDownload: z.boolean(),
    downloadUrl: z.string().min(1).nullable(),
    file: PublicFileShareFile.nullable(),
    folder: PublicFileShareFolder.nullable(),
    ancestors: z.array(PublicFileShareAncestor),
    childFolders: z.array(PublicFileShareFolder),
    childFiles: z.array(PublicFileShareFile),
    totalFolders: z.number().int().nonnegative(),
    totalFiles: z.number().int().nonnegative(),
    expiresAt: Iso.nullable(),
});
export const FileShareAccessLog = z.object({
    id: z.string().uuid(),
    shareId: z.string().uuid(),
    fileId: z.string().uuid().nullable(),
    folderId: z.string().uuid().nullable(),
    viewerUserId: z.string().uuid().nullable(),
    action: z.enum(['view', 'download']),
    success: z.boolean(),
    failureReason: z.string().max(120).nullable(),
    bytesServed: z.number().int().nonnegative().nullable(),
    rangeStart: z.number().int().nonnegative().nullable(),
    rangeEnd: z.number().int().nonnegative().nullable(),
    createdAt: Iso,
});
export const CreateFileShareRequest = z.object({
    fileId: z.string().uuid().optional(),
    folderId: z.string().uuid().optional(),
    title: z.string().min(1).max(255).nullable().optional(),
    passcode: z.string().min(1).max(128).nullable().optional(),
    allowDownload: z.boolean().optional(),
    maxAccessCount: z.number().int().positive().max(1_000_000).nullable().optional(),
    availableAt: Iso.nullable().optional(),
    expiresAt: Iso.nullable().optional(),
});
export const UpdateFileShareRequest = z.object({
    title: z.string().min(1).max(255).nullable().optional(),
    passcode: z.string().min(1).max(128).nullable().optional(),
    allowDownload: z.boolean().optional(),
    maxAccessCount: z.number().int().positive().max(1_000_000).nullable().optional(),
    availableAt: Iso.nullable().optional(),
    expiresAt: Iso.nullable().optional(),
});
export const ReplaceFileShareScheduleRequest = z.object({
    items: z
        .array(z.object({
        targetKind: z.enum(['file', 'folder']).optional(),
        fileId: z.string().uuid().optional(),
        folderId: z.string().uuid().optional(),
        releaseAt: Iso,
    }))
        .max(365)
        .optional(),
    fileIds: z.array(z.string().uuid()).max(365).optional(),
    startAt: Iso.optional(),
    cadenceDays: z.number().int().positive().max(365).optional(),
});
export const AccessFileShareRequest = z.object({
    passcode: z.string().min(1).max(128).optional(),
});
export const FileShareResponse = z.object({ share: FileShare });
export const CreateFileShareResponse = z.object({
    share: FileShare,
    token: z.string().min(32),
    url: z.string().min(1),
});
export const FileShareListResponse = z.object({
    shares: z.array(FileShare),
    total: z.number().int().nonnegative(),
    limit: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
});
export const FileShareScheduleResponse = z.object({
    items: z.array(FileShareScheduledTarget),
});
export const FileShareScheduleCandidateListResponse = z.object({
    items: z.array(FileShareScheduleCandidate),
});
export const PublicFileShareResponse = z.object({ share: PublicFileShare });
export const FileShareAccessLogListResponse = z.object({
    logs: z.array(FileShareAccessLog),
    total: z.number().int().nonnegative(),
    limit: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
});
//# sourceMappingURL=share.js.map